// frontend/features/mpc-common/src/features/Bidding/SupplierQuoteDrawer/index.tsx
import React, { useState } from 'react';
import { Button, Message, Modal } from '@bedrock/components';
import { Edit, Eye } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import BiddingSupplierQuoteDrawer from './Drawer';
import { biddingGetSupplierQuoteDetail } from '@mpc/common/src/services/api';
import type { ViewMode } from './types';

interface Props {
  /** 比选项目详情(detail).id 即 demand_id (注: bidding 业务沿用 demand 字段名) */
  detail: {
    id: number;
    current_bidding_supplier_id?: number;
    bidding_suppliers?: any[];
    [k: string]: any;
  };
  /** 触发回显的回调: 抽屉提交校验通过后返回 tabs 数据 */
  onCommit?: (tabs: Record<string, any>[]) => void;
  /** 预留:历史轮次报价 id,本任务不接入 UI */
  quoteId?: number;
  /** 默认 'edit',外部可强制只读 */
  viewMode?: ViewMode;
  /** 按钮文案 */
  buttonText?: string;
  /** 预取的 tabs 数据，传入后跳过 API 请求直接回显抽屉 */
  initialTabs?: Record<string, any>[];
}

const BiddingSupplierQuoteButton: React.FC<Props> = ({
  detail,
  onCommit,
  quoteId,
  viewMode = 'edit',
  buttonText,
  initialTabs,
}) => {
  const [loading, setLoading] = useState(false);
  const demandId = detail.id;
  const supplierId =
    detail.current_bidding_supplier_id ??
    detail.bidding_suppliers?.[0]?.supplier_id;

  const openDrawer = useMemoizedFn(async () => {
    if (!supplierId) {
      Message.error('未找到当前供应商');
      return;
    }
    setLoading(true);
    try {
      let initialData: any;
      if (initialTabs?.length) {
        // 预取数据：直接使用 initialTabs
        initialData = {
          tabs: initialTabs,
          last_temp_save_time: null,
        };
      } else {
        const res = await biddingGetSupplierQuoteDetail({
          id: demandId,
          sid: supplierId,
        });
        initialData = {
          tabs: res.data?.tabs ?? [],
          quote_total_amount: res.data?.quote_total_amount,
          status: res.data?.status,
          last_temp_save_time: null,
        };
      }

      const { close } = BiddingSupplierQuoteDrawer.show({
        supplierId,
        demandId,
        viewMode,
        initialData,
        onCommit: (value) => {
          if (value) {
            onCommit?.(value);
            close();
          }
        },
      });
    } catch (e: any) {
      Message.error(e?.message || '获取报价明细失败');
    } finally {
      setLoading(false);
    }
  });

  const text =
    buttonText ?? (viewMode === 'edit' ? '填写明细' : '查看报价明细');
  const icon = viewMode === 'edit' ? <Edit /> : null;

  return (
    <Button
      loading={loading}
      type={viewMode === 'edit' ? 'checked' : 'link'}
      color="primary"
      icon={icon}
      onClick={openDrawer}
    >
      {text}
    </Button>
  );
};

export default BiddingSupplierQuoteButton;
